import React from "react";
import { Checkbox, Form, Input, Select, Space, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { UTM_MEDIUMS, UTM_SOURCES } from "constants/MarketingConstants";

const { Option } = Select;

const UTMFormFields = ({
  form,
  useCustomUrl,
  setUseCustomUrl,
  showShopSource = true,
  shopSourceDisabled = false,
  namePrefix = [],
}) => {
  const { t } = useTranslation();

  const utmTooltips = {
    name: t("explanations.utm_tooltip_name"),
    source: t("explanations.utm_tooltip_source"),
    medium: t("explanations.utm_tooltip_medium"),
    campaign: t("explanations.utm_tooltip_campaign"),
    term: t("explanations.utm_tooltip_term"),
    content: t("explanations.utm_tooltip_content"),
  };

  const renderFormItemWithTooltip = (label, tooltip, children) => (
    <Form.Item
      label={
        <Space>
          {label}
          <Tooltip title={tooltip} placement="right">
            <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
          </Tooltip>
        </Space>
      }
    >
      {children}
    </Form.Item>
  );

  return (
    <>
      {showShopSource && (
        <Form.Item
          label={t("shop_source")}
          name={[...namePrefix, "shopSource"]}
          rules={[{ required: true, message: t("please_select_shop_source") }]}
        >
          <Select placeholder={t("select_shop_source")} disabled={shopSourceDisabled}>
            <Option value="VCE">VCE - Vleescentrum</Option>
            <Option value="HVO">HVO - Halalvlees Online</Option>
          </Select>
        </Form.Item>
      )}

      <Form.Item style={{ marginBottom: 8 }}>
        <Checkbox
          checked={useCustomUrl}
          onChange={(e) => {
            setUseCustomUrl(e.target.checked);
            if (!e.target.checked && form) {
              if (namePrefix.length > 0) {
                form.setFields([{ name: [...namePrefix, "customUrl"], value: "" }]);
              } else {
                form.setFieldsValue({ customUrl: "" });
              }
            }
          }}
        >
          <Space>
            {t("use_custom_page_url")}
            <Tooltip title={t("use_custom_page_url_tooltip")} placement="right">
              <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
            </Tooltip>
          </Space>
        </Checkbox>
      </Form.Item>

      {useCustomUrl && (
        <Form.Item
          label={t("custom_page_url")}
          name={[...namePrefix, "customUrl"]}
          help={t("custom_url_help")}
          style={{ paddingBottom: 15 }}
        >
          <Input placeholder="https://hetvleescentrum.nl/kerst-specials" />
        </Form.Item>
      )}

      {renderFormItemWithTooltip(
        t("utm_name"),
        utmTooltips.name,
        <Form.Item
          name={[...namePrefix, "name"]}
          rules={[{ required: true, message: t("please_enter_utm_name") }]}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder={t("enter_utm_name")} />
        </Form.Item>,
      )}

      {renderFormItemWithTooltip(
        t("utm_source"),
        utmTooltips.source,
        <Form.Item
          name={[...namePrefix, "sourceType"]}
          rules={[{ required: true, message: t("please_select_source") }]}
          style={{ marginBottom: 0 }}
        >
          <Select placeholder={t("select_source")}>
            {UTM_SOURCES.map((source) => (
              <Option key={source.value} value={source.value}>
                {source.label}
              </Option>
            ))}
          </Select>
        </Form.Item>,
      )}

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => {
          const prevSource = namePrefix.length ? prevValues[namePrefix[0]]?.sourceType : prevValues.sourceType;
          const currentSource = namePrefix.length ? currentValues[namePrefix[0]]?.sourceType : currentValues.sourceType;
          return prevSource !== currentSource;
        }}
      >
        {({ getFieldValue }) => {
          const sourceType = namePrefix.length
            ? getFieldValue([...namePrefix, "sourceType"])
            : getFieldValue("sourceType");
          return sourceType === "other" ? (
            <Form.Item
              label={t("custom_source")}
              name={[...namePrefix, "sourceCustom"]}
              rules={[{ required: true, message: t("please_enter_custom_source") }]}
            >
              <Input placeholder={t("enter_custom_source")} />
            </Form.Item>
          ) : null;
        }}
      </Form.Item>

      {renderFormItemWithTooltip(
        t("utm_medium"),
        utmTooltips.medium,
        <Form.Item
          name={[...namePrefix, "mediumType"]}
          rules={[{ required: true, message: t("please_select_medium") }]}
          style={{ marginBottom: 0 }}
        >
          <Select placeholder={t("select_medium")}>
            {UTM_MEDIUMS.map((medium) => (
              <Option key={medium.value} value={medium.value}>
                {medium.label}
              </Option>
            ))}
          </Select>
        </Form.Item>,
      )}

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => {
          const prevMedium = namePrefix.length ? prevValues[namePrefix[0]]?.mediumType : prevValues.mediumType;
          const currentMedium = namePrefix.length ? currentValues[namePrefix[0]]?.mediumType : currentValues.mediumType;
          return prevMedium !== currentMedium;
        }}
      >
        {({ getFieldValue }) => {
          const mediumType = namePrefix.length
            ? getFieldValue([...namePrefix, "mediumType"])
            : getFieldValue("mediumType");
          return mediumType === "other" ? (
            <Form.Item
              label={t("custom_medium")}
              name={[...namePrefix, "mediumCustom"]}
              rules={[{ required: true, message: t("please_enter_custom_medium") }]}
            >
              <Input placeholder={t("enter_custom_medium")} />
            </Form.Item>
          ) : null;
        }}
      </Form.Item>

      {renderFormItemWithTooltip(
        t("campaign_name"),
        utmTooltips.campaign,
        <Form.Item
          name={[...namePrefix, "campaign"]}
          rules={[{ required: true, message: t("please_enter_campaign_name") }]}
          help={t("campaign_name_help")}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder={t("enter_campaign_name_example")} />
        </Form.Item>,
      )}

      {renderFormItemWithTooltip(
        t("utm_term"),
        utmTooltips.term,
        <Form.Item name={[...namePrefix, "term"]} help={t("utm_term_help")} style={{ marginBottom: 0 }}>
          <Input placeholder={t("enter_utm_term_example")} />
        </Form.Item>,
      )}

      {renderFormItemWithTooltip(
        t("utm_content"),
        utmTooltips.content,
        <Form.Item name={[...namePrefix, "content"]} help={t("utm_content_help")} style={{ marginBottom: 0 }}>
          <Input placeholder={t("enter_utm_content_example")} />
        </Form.Item>,
      )}
    </>
  );
};

export default UTMFormFields;
